// app-pages/Demo.tsx
"use client";
import { Flex, Text, Grid, Box, Button, useDisclosure, chakra } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { S3Client, ListObjectsV2Command } from "@aws-sdk/client-s3"; // Import from AWS SDK v3
import Im from "./Im";
import { GoogleForm, FilterOption } from "components";
import Placeholder from "@public/Dark.webp";
import { useRouter } from "next/navigation";
import { useInView } from "react-intersection-observer";
import { throttle } from "lodash";

const REGION = process.env.NEXT_PUBLIC_AWS_REGION;
const ACCESS_KEY_ID = process.env.NEXT_PUBLIC_AWS_ACCESS_KEY_ID;
const SECRET_ACCESS_KEY = process.env.NEXT_PUBLIC_AWS_SECRET_ACCESS_KEY;

if (REGION === undefined) {
  throw new Error("NEXT_PUBLIC_AWS_REGION not set");
}

if (ACCESS_KEY_ID === undefined) {
  throw new Error("NEXT_PUBLIC_AWS_ACCESS_KEY_ID not set");
}

if (SECRET_ACCESS_KEY === undefined) {
  throw new Error("NEXT_PUBLIC_AWS_SECRET_ACCESS_KEY not set");
}

const cloudFrontDomain = 'https://d2dd7cy0lf5lc.cloudfront.net';

const s3 = new S3Client({
  region: REGION,
  credentials: {
    accessKeyId: ACCESS_KEY_ID,
    secretAccessKey: SECRET_ACCESS_KEY,
  },
});




type NavigationItem = {
  path: string;
  contents: any[];
  title: string;
  depth: number;
  isAtLastDepth: boolean;
  inLastFolder: boolean;
};

type S3Content = {
  Key: string;
  LastModified: string;
  ETag: string;
  Size: number;
  StorageClass: string;
};

type ImageGenerationCountType = {
  [key: string]: number;
};

// Function to extract the base name (file or folder) from the S3 key
const extractName = (key: string, depth: number): string => {
  const parts = key.split("/");
  if (depth === 1) {
    // Extract the folder name for depth 1
    return parts[1].split(".")[0];
    
  } else {
    // Extract the file name without the extension for depth 2
    const fileName = parts[parts.length - 1];
    return fileName.split(".")[0];
  }
};

// Function to reorder the contents based on special criteria and depth
const reorderContents = (contents: S3Content[], depth: number): S3Content[] => {
  const SPECIAL_ORDERS: { [key: number]: string[] } = {
    1: ["Hoppy Floppy", "Lucy Dream", "Hitomi", "Blac Chyna"],
    2: ["Realistic", "Digital", "Anime"],
  };

  const specialOrder = SPECIAL_ORDERS[depth] || [];

  // Reorder contents based on special order
  const orderedContents = specialOrder
    .map((orderName) => contents.find((content) => extractName(content.Key, depth) === orderName))
    .filter((item): item is S3Content => item !== undefined); // Filter out any undefined items

  // Append any other items that were not in the special order
  const otherItems = contents.filter(
    (content) => !specialOrder.includes(extractName(content.Key, depth))
  );

  return [...orderedContents, ...otherItems];
};

export function Demo() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "0px 0px",
  });

  const [currentPath, setCurrentPath] = useState("Models/Hitomi/Realistic/Bikini/Bedroom/Blonde/");
  const [lastDepth, setLastDepth] = useState(false);

  const [navigationHistory, setNavigationHistory] = useState<NavigationItem[]>([
    {
      path: "Models/",
      contents: [
        {
          Key: "Models/Hoppy Floppy.webp",
        },
        {
          Key: "Models/Hitomi.webp",
        },
        {
          Key: "Models/Aitana.webp",
        },
      ],
      title: "",
      depth: 1,
      isAtLastDepth: false,
      inLastFolder: false,
    },
    {
      path: "Models/Hitomi/",
      contents: [
        {
          Key: "Models/Hitomi/Realistic.webp",
        },
        {
          Key: "Models/Hitomi/Digital.webp",
        },
        {
          Key: "Models/Hitomi/Anime.webp",
        },
      ],
      title: "Choose your style",
      depth: 2,
      isAtLastDepth: false,
      inLastFolder: false,
    },
    {
      path: "Models/Hitomi/Realistic/",
      contents: [
        {
          Key: "Models/Hitomi/Realistic/Bikini/",
        },
        {
          Key: "Models/Hitomi/Realistic/Latex/",
        },
        {
          Key: "Models/Hitomi/Realistic/Lingerie/",
        },
        {
          Key: "Models/Hitomi/Realistic/Robe/",
        },
      ],
      title: "Outfit",
      depth: 3,
      isAtLastDepth: false,
      inLastFolder: false,
    },
    {
      path: "Models/Hitomi/Realistic/Bikini/",
      contents: [
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/City/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Luxury/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Nature/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Vacation/",
        },
      ],
      title: "Themes",
      depth: 4,
      isAtLastDepth: false,
      inLastFolder: false,
    },
    {
      path: "Models/Hitomi/Realistic/Bikini/Bedroom/",
      contents: [
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/Blonde/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/Ginger/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/Original/",
        },
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/Pink/",
        },
      ],
      title: "Hair color",
      depth: 5,
      isAtLastDepth: false,
      inLastFolder: false,
    },
    {
      path: "Models/Hitomi/Realistic/Bikini/Bedroom/Blonde/",
      contents: [
        {
          Key: "Models/Hitomi/Realistic/Bikini/Bedroom/Blonde/00000-erotic photo of (ohwx woman) with blonde hair in (bikini), bedroom setting.png",
        },
      ],
      title: "",
      depth: 6,
      isAtLastDepth: true,
      inLastFolder: true,
    },
  ]);

  const [imageGenerationCount, setImageGenerationCount] = useState<ImageGenerationCountType>({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImages, setShowImages] = useState(false);

  const [selectedPaths, setSelectedPaths] = useState<string[]>([
    "/Models/Hitomi",
    "Models/Hitomi/",
    "Models/Hitomi/Realistic/",
    "Models/Hitomi/Realistic/Bikini/",
    "Models/Hitomi/Realistic/Bikini/Bedroom/",
    "Models/Hitomi/Realistic/Bikini/Bedroom/Blonde/",
  ]);

  const [fullImageList, setFullImageList] = useState<string[]>([]);
  const [isLoading, setisLoading] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showMessage, setShowMessage] = useState(false);
  const router = useRouter();

  type BaseTitleMap = {
    [key: string]: string;
  };

  const folderTitleMapping: BaseTitleMap = {
    Blonde: "Hair color",
    Dark: "Hair color",
    Pink: "Hair color",
    Ginger: "Hair color",
    Original: "Hair color",
    Bedroom: "Themes",
    Nature: "Themes",
    Luxury: "Themes",
    Street: "Themes",
    City: "Themes",
    Vacation: "Themes",
    Anime: "Choose your style",
    Digital: "Choose your style",
    Realistic: "Choose your style",
    Robe: "Outfit",
    Lingerie: "Outfit",
    Latex: "Outfit",
    Bikini: "Outfit",
    Cyberpunk: "Characters",
    Fantasy: "Characters",
    "Sci-Fi": "Characters",
    Royalty: "Characters",
    GTA: "Characters",
    Slim: "Extras",
    "Hoppy Floppy": "Choose your style",
    "Lucy Dream": "Choose your style",
    Hitomi: "Choose your style",
    "Blac Chyna": "Choose your style",
  };

  const getDepth = (path: string) => {
    return path.split("/").filter(Boolean).length;
  };

  useEffect(() => {
    const fetchObjectsThrottled = throttle(async () => {
      const command = new ListObjectsV2Command({
        Bucket: "deepmodeassets",
        Prefix: currentPath,
        Delimiter: "/",
      });

      try {
        const data = await s3.send(command);
        const filteredContents = data.Contents?.filter((item) => !item.Key?.endsWith(".DS_Store"));
        setisLoading(false);
        setLastDepth(false);

        let newContents: any = [];
        const depth = getDepth(currentPath);

        // Check if there are no more subdirectories
        const isLastDepth = !data.CommonPrefixes || data.CommonPrefixes.length === 0;

        if (filteredContents?.length) {
          if (isLastDepth) {
            setLastDepth(true);
            const imageKeys = filteredContents
              .map((content) => content.Key)
              .filter((key) => key !== undefined) as string[];
            const cloudFrontUrls = imageKeys.map(key => `${key}`);
            setFullImageList(cloudFrontUrls);
            newContents = filteredContents.slice(0, 1);
          } else {
            newContents = filteredContents;
          }
        } else if (data.CommonPrefixes) {
          const directories = data.CommonPrefixes.map((prefix) => ({
            Key: prefix.Prefix,
          }));
          newContents = directories;
        }

        if (depth < 3) {
          newContents = reorderContents(newContents, depth);
        }

        setNavigationHistory((prevHistory) => {
          const currentDepth = depth;
          const newTitle = isLastDepth ? "" : getTitleForFolders(newContents);

          const updatedHistory = prevHistory.filter((item) => item.depth < currentDepth);
          const existingItem = updatedHistory.find((item) => item.path === currentPath);
          if (existingItem) {
            existingItem.contents = newContents;
            existingItem.title = newTitle;
            existingItem.isAtLastDepth = isLastDepth;
          } else {
            updatedHistory.push({
              path: currentPath,
              contents: newContents,
              title: newTitle,
              depth: currentDepth,
              isAtLastDepth: isLastDepth,
              inLastFolder: isLastDepth,
            });
          }

          return updatedHistory;
        });
      } catch (err) {
        console.log(err);
      }
    }, 1000);

    if (inView) {
      fetchObjectsThrottled();
    }
  }, [currentPath, inView]);

  useEffect(() => {
    // Whenever currentPath changes, update the selectedPaths
    setSelectedPaths((prevSelectedPaths) => {
      const depth = getDepth(currentPath);
      // Slice the array to keep only paths up to the current depth and add the new path
      const newSelectedPaths = prevSelectedPaths.slice(0, depth - 1);
      newSelectedPaths.push(currentPath);
      return newSelectedPaths;
    });
  }, [currentPath]);

  const handleNavigate = (key: any) => {
    setShowImages(false);
    setisLoading(true);

    // Determine the new depth for the key
    const newDepth = getDepth(key);

    if (key.includes(".jpg") || key.includes(".png") || key.includes(".webp") ) {
      // It's an image navigation
      const newPath = key.split(".").slice(0, -1).join(".") + "/";
      setCurrentPath(newPath);
      setSelectedPaths((prev) => {
        const newPath = prev.slice(0, newDepth); // Include the new depth for images
        newPath.push(sanitizePath(key)); // Push the sanitized image path
        return newPath;
      });
    } else if (key.endsWith("/")) {
      // It's a folder navigation
      setCurrentPath(key);
      setSelectedPaths((prev) => {
        const newPath = prev.slice(0, newDepth - 1); // Trim the array to the parent folder's depth
        newPath.push(key); // Add the new path
        return newPath;
      });
    }
  };

  const handleRegenerateClick = () => {
    setisLoading(true);
    const nextImageIndex =
      currentImageIndex + 1 >= fullImageList.length ? 0 : currentImageIndex + 1;
    if (currentImageIndex + 1 >= fullImageList.length) {
      onOpen();
      return;
    }
    // Update the current image index
    setCurrentImageIndex(nextImageIndex);

    // Slice the next set of images
    const newContentsSlice = fullImageList.slice(nextImageIndex, nextImageIndex + 1);

    // Update the navigation history with the new slice for the current item
    setNavigationHistory((prevHistory) => {
      return prevHistory.map((item) => {
        if (item.path === currentPath) {
          // We found the current navigation item, so let's update its contents
          return {
            ...item,
            contents: newContentsSlice.map((key) => ({ Key: key })),
          };
        }
        return item; // For all other items, return them as they were
      });
    });
  };

  function getTitleForFolders(folders: any[]): string {
    let potentialTitles = new Set();
    for (let path of folders) {
      const segments = path.Key.split("/");
      let lastFolder = "";

      if (path.Key[path.Key.lenght - 1] === "/") {
        lastFolder = segments[segments.length - 2];
      } else {
        lastFolder = segments[segments.length - 2].split(".")[0];
      }
      const title = folderTitleMapping[lastFolder];
      if (title) {
        potentialTitles.add(title);
      }
    }

    if (potentialTitles.size === 1) {
      return Array.from(potentialTitles)[0] as string; // Return the single title found
    } else {
      return ""; // Handle cases where there might be multiple potential titles or no titles
    }
  }

  const sanitizePath = (path: string) => {
    // If path ends with a slash, it's a directory, so don't change it.
    // Otherwise, remove the file extension if there is one.
    return path.endsWith("/") ? path : path.replace(/\.\w+$/, "");
  };

  const handleImageGeneration = (path: string) => {
    setShowMessage(false);
    setImageGenerationCount((prevCount: ImageGenerationCountType) => ({
      ...prevCount,
      [path]: (prevCount[path] || 0) + 1,
    }));
  };

  return (
    <>
      <Flex mt={{base:"30px", md:"100px"}}  px={{ base: "20px", sm: "35px", md: "40px", lg: "60px", xl: "60px", "2xl": "12%" }}
 w="100%" direction="column">
        <Flex minH="600px" direction="column">
          <Flex
            w={{ base: "290px", md: "720px", lg: "100%" }}
            mt={{ base: "100px", lg: "0px" }}
            textAlign={{ base: "start", md: "start", lg: "center" }}
            fontWeight="semibold"
            justify="center"
            fontSize={{ base: "32px", md: "44px", "2xl": "48px" }}
            as={"h2"}
          >
            <Text>
              Try our <chakra.span color="#FA1B6B"> Image Generation </chakra.span>Demo
            </Text>
          </Flex>

          <Text
            fontSize={{ base: "14px", lg: "24px" }}
            fontWeight="semibold"
            textAlign={{ base: "start", lg: "center" }}
            color="white"
            mt={{ base: "16px" }}
            ref={ref}
            as={"h4"}
          >
            Choose your model and tags
          </Text>
          {navigationHistory.map((navItem) => {
            if (navItem.depth === 1) {
              return (
                <Flex w="100%" direction="column" key={navItem.path}>
                  <Text color="#D5D5D5" fontSize="14px" fontWeight="bold" mb={4}>
                    {navItem.title}
                  </Text>
                  <Grid
                    templateColumns={{
                      base: "repeat(2,1fr)",
                      lg: "repeat(4,1fr)",
                    }}
                    key={navItem.path}
                    gap={3}
                    w="100%"
                  >
                    {" "}
                    {navItem.contents?.map((content) => (
                      <Box key={content.Key}>
                        {content.Key.endsWith("/") ? (
                          <FilterOption
                            selected={navigationHistory.some(
                              (historyItem) => historyItem.path === content.Key
                            )}
                            onClick={
                              navItem.isAtLastDepth ? () => {} : () => handleNavigate(content.Key)
                            }
                            text={
                              navItem.isAtLastDepth
                                ? ""
                                : content.Key.replace(navItem.path, "").replace("/", "")
                            }
                            bgColor=""
                          />
                        ) : (
                            <Im
                                  selected={selectedPaths.includes(sanitizePath(content.Key) + "/")}
                                  onClick={
                                    navItem.isAtLastDepth ? () => {} : () => handleNavigate(content.Key)
                                  }
                                  isLoading={
                                    navItem.depth ===
                                      navigationHistory[navigationHistory.length - 1].depth && lastDepth
                                  }
                                  source={`${cloudFrontDomain}/${content.Key}`}
                                  alt={`Image of ${sanitizePath(content.Key.replace(navItem.path, "").replace("/", ""))}`} // Pass alt here
                                  text={
                                    navItem.depth ===
                                      navigationHistory[navigationHistory.length - 1].depth && lastDepth
                                      ? ""
                                      : sanitizePath(
                                          content.Key.replace(navItem.path, "").replace("/", "")
                                        )
                                  }
                                  showInstagram={navItem.depth === 1}
                                  show={
                                    (navItem.isAtLastDepth && showImages && !isLoading) ||
                                    !navItem.isAtLastDepth
                                  }
                              />

                        )}
                      </Box>
                    ))}
                    <Box>
                      <Im
                        selected={false}
                        onClick={() => onOpen()}
                        isLoading={false}
                        source={Placeholder.src}
                        alt="Placeholder image for your model"  // Add alt text here
                        text={"Your Model"}
                        showInstagram={false}
                        show={
                          (navItem.isAtLastDepth && showImages && !isLoading) ||
                          !navItem.isAtLastDepth
                        }
                      />

                    </Box>
                  </Grid>
                </Flex>
              );
            }

            return null;
          })}
          <Flex
            w="100%"
            direction={{ base: "column", lg: "row" }}
            mt={{ base: "40px", "2xl": "65px" }}
          >
            {navigationHistory.length > 1 && (
              <Flex
                direction="column"
                borderRadius="20px"
                p={{ base: "15px", lg: "32px" }}
                w={{ base: "100%", lg: "50%" }}
                bg="#040506"
                border="1px solid gray"
                justify="center"
                align="center"
                maxH={{ "2xl": "100%" }}
              >
                {navigationHistory.map((navItem, idx) => {
                  if (navItem.depth !== 1) {
                    return (
                      <Flex direction="column" key={navItem.path} align="start" w="100%">
                        <Text
                          color="#D5D5D5"
                          fontSize="14px"
                          fontWeight="bold"
                          mb={4}
                          mt={idx > 1 ? "20px" : 0}
                        >
                          {navItem.title}
                        </Text>
                        <Flex gap={3} wrap={idx > 1 ? "wrap" : "nowrap"} w="100%">
                          {" "}
                          {navItem.contents?.map((content, index) => (
                            <Flex key={content.Key}>
                              <Box>
                                {content.Key.endsWith("/") ? (
                                  <FilterOption
                                    selected={navigationHistory.some(
                                      (historyItem) => historyItem.path === content.Key
                                    )}
                                    onClick={
                                      navItem.isAtLastDepth
                                        ? () => {}
                                        : () => handleNavigate(content.Key)
                                    }
                                    text={
                                      navItem.isAtLastDepth
                                        ? ""
                                        : content.Key.replace(navItem.path, "").replace("/", "")
                                    }
                                    bgColor=""
                                  />
                                ) : (
                                  !navItem.isAtLastDepth && (
                                    <Flex
                                      align="center"
                                      direction="column"
                                      maxW={{ base: "80px", lg: "" }}
                                    >
                                      <FilterOption
                                        text={sanitizePath(
                                          content.Key.replace(navItem.path, "").replace("/", "")
                                        )}
                                        selected={selectedPaths.includes(
                                          sanitizePath(content.Key) + "/"
                                        )}
                                        onClick={
                                          navItem.isAtLastDepth
                                            ? () => {}
                                            : () => handleNavigate(content.Key)
                                        }
                                        bgColor=""
                                      />
                                      <Box mt="20px">
                                      <Im
                                          onClick={
                                            navItem.isAtLastDepth
                                              ? () => {}
                                              : () => handleNavigate(content.Key)
                                          }
                                          source={`${cloudFrontDomain}/${content.Key}`}
                                          alt={`Image of ${sanitizePath(content.Key.replace(navItem.path, "").replace("/", ""))}`}  // Add alt text here
                                          showInstagram={false}
                                          text={""}
                                          isLoading={false}
                                          show={!navItem.isAtLastDepth}
                                          selected={false}
                                        />

                                      </Box>
                                    </Flex>
                                  )
                                )}
                              </Box>
                              {index === navItem.contents.length - 1 &&
                                content.Key.endsWith("/") && (
                                  <Box ml="12px">
                                    <FilterOption
                                      selected={false} // Set the selected state as needed
                                      onClick={() => {
                                        setShowMessage(true);
                                      }}
                                      text="More +"
                                      bgColor="" // Set the background color as needed
                                    />
                                  </Box>
                                )}
                            </Flex>
                          ))}
                        </Flex>
                      </Flex>
                    );
                  }
                })}
                <Button
                  display={lastDepth ? "block" : "none"}
                  onClick={() => {
                    if (showImages) {
                      handleRegenerateClick();
                    } else {
                      setCurrentImageIndex(0);
                      setShowImages(true);
                    }
                    handleImageGeneration(currentPath);
                  }}
                  bg="button.500"
                  color="white"
                  w="100%"
                  h="50px"
                  mt={{ lg: "5px", "2xl": "20px" }}
                  p="16px, 30px, 16px, 30px"
                  fontSize={{ base: "16px", "2xl": "20px" }}
                  isDisabled={imageGenerationCount[currentPath] >= 4}
                >
                  Create{" "}
                </Button>
                <GoogleForm isOpen={isOpen} onClose={onClose} />
              </Flex>
            )}
            {navigationHistory.length > 1 && (
              <Flex
                direction="column"
                ml={{ base: "0", lg: "24px" }}
                borderRadius="20px"
                w={{ base: "100%", lg: "50%" }}
                p={{ base: "15px", lg: "32px" }}
                bg="#040506"
                mt={{ base: "20px", lg: "0px" }}
                border="1px solid gray"
                justify="center"
                align="center"
                maxH={{ "2xl": "100%" }}
              >
                {showMessage ? (
                  <>
                    <Text
                      fontSize="24px"
                      fontWeight="bold"
                      style={{
                        background: "linear-gradient(to right,  #FFB5FF 0%, #1AA5D3 50%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        MozBackgroundClip: "text",
                      }}
                    >
                      Join us to unlock more
                    </Text>
                    <Text textAlign="center" mt="15px" color="gray" fontSize="14px">
                      Unlock the full power of our AI image generation tool. Get advanced features
                      and receive Free Trial credits.{" "}
                    </Text>
                    <Button onClick={() => onOpen()} mt="20px" color="white" bg="#FF1B6D">
                      Join us
                    </Button>
                  </>
                ) : lastDepth && showImages ? (
                  <>
                    {navigationHistory[navigationHistory.length - 1].contents.map((content) => (
                      <Im
                      source={`${cloudFrontDomain}/${content.Key}`}
                      alt={`Image of model`}  // Add alt text here
                      text=""
                      show={showImages}
                      showInstagram={false}
                      isLoading={true}
                      selected={false}
                      onClick={() => console.log()}
                      key={content}
                    />
                    
                    ))}
                  </>
                ) : (
                  <Text fontSize="24px" fontWeight="semibold">
                    Choose your tags and Create
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}

export default Demo;
